/**
 * Sorting utilities
 */

export interface OrderedItem {
  order_by: number;
  uuid?: string;
  [key: string]: unknown;
}

/**
 * Sorts array of strings using locale comparison
 */
export const customSort = <T extends string>(data: T[]): T[] => {
  return (data || []).sort((a, b) => {
    return (a || '')?.toString()?.localeCompare(b, undefined, { sensitivity: 'base' });
  });
};

/**
 * Sorts array of objects by a specific key using locale comparison
 */
export const customSortByKey = <T extends Record<string, unknown>>(data: T[], key: string): T[] => {
  return (data || []).sort((a, b) => {
    return ((a?.[key] as string) || '')
      ?.toString()
      ?.localeCompare(b?.[key] as string, undefined, { sensitivity: 'base' });
  });
};

/**
 * Sorts array of objects by a specific key in ascending order
 */
export const customSortByKeyAsc = <T extends Record<string, unknown>>(
  data: T[],
  key: string
): T[] => {
  return (data || []).sort((a, b) => {
    return ((a?.[key] as string) || '')
      ?.toString()
      ?.localeCompare(b?.[key] as string, undefined, { sensitivity: 'base' });
  });
};

/**
 * Gets sorted data by order_by field
 */
export const getSortedData = <T extends OrderedItem>(data: Record<string, T>): T[] => {
  return (Object.values(data || {}) || []).sort((a, b) => a.order_by - b.order_by);
};

/**
 * Gets sorted UUIDs by order_by field
 */
export const getSortedKeys = <T extends OrderedItem>(data: Record<string, T>): string[] => {
  const values = Object.values(data) ?? [];
  const sorted = values.sort((a, b) => a.order_by - b.order_by);
  const uuid_list = sorted?.map((item) => item.uuid!);
  return uuid_list;
};
